import { v4 as uuidv4 } from 'uuid';
import { IMergeRows, IMergeRowsAction } from '@models/Forms/IForms';
import { TableRow } from './tableColumns';

export interface IMergeGroup {
    result: TableRow;
    items: TableRow[];
}

const groupKeyOf = (keys: string[], row: TableRow): string =>
    keys.map((key) => (row[key] != null ? String(row[key]) : '')).join('');

/** Группировка строк для объединения (паритет InitData MergeRowsForm):
 *  строки с одинаковыми settings.keys сливаются, поля-агрегаты считаются
 *  по actions (sum/concat/max/min/avg). */
export function buildMergeGroups(rows: TableRow[], settings: IMergeRows): Record<string, IMergeGroup> {
    const map: Record<string, IMergeGroup> = {};
    const actions = settings.actions?.actions ?? [];
    for (const row of rows) {
        const key = groupKeyOf(settings.keys?.keys ?? [], row);
        const group = map[key];
        if (!group) {
            map[key] = { result: { ...row }, items: [{ ...row }] };
            continue;
        }
        group.items.push({ ...row });
        actions.forEach((action: IMergeRowsAction) => {
            const acc = group.result[action.column] as never;
            const value = row[action.column] as never;
            switch (action.type) {
                case 'sum':
                case 'avg':
                    group.result[action.column] = (acc as number) + (value as number);
                    break;
                case 'concat':
                    group.result[action.column] =
                        `${acc != null && acc !== '' ? `${String(acc)}|` : ''}${String(value)}`;
                    break;
                case 'max':
                    if (value > acc) group.result[action.column] = value;
                    break;
                case 'min':
                    if (value < acc) group.result[action.column] = value;
                    break;
                default:
                    break;
            }
        });
    }
    const avgActions = actions.filter((action) => action.type === 'avg');
    for (const key of Object.keys(map)) {
        const count = map[key].items.length;
        avgActions.forEach((action) => {
            map[key].result[action.column] = (map[key].result[action.column] as number) / count;
        });
    }
    return map;
}

/** Строки предпросмотра: результат группы + исходные строки в '|Merged'
 *  (master-detail предпросмотра; null — группа из одной строки). */
export function mergePreviewRows(groups: Record<string, IMergeGroup>): TableRow[] {
    return Object.keys(groups).map((key, index) => ({
        ...groups[key].result,
        '|NUM': `preview-${index + 1}`,
        '|Merged': groups[key].items.length > 1 ? groups[key].items : null,
    }));
}

/** Итоговые строки объединения. ВАЖНО: легаси-форма ставила целочисленные
 *  |NUM — здесь ключи нормализуются в uuid (контракт getRowId таблицы). */
export function mergeResultRows(groups: Record<string, IMergeGroup>): TableRow[] {
    return Object.keys(groups).map((key) => {
        const item: TableRow = { ...groups[key].result, '|NUM': uuidv4() };
        delete item['|Merged'];
        return item;
    });
}

/** Тиражирование (паритет onSplit): каждая выбранная строка разворачивается в
 *  N строк по вариантам — значения полей варианта дописываются суффиксом
 *  « (значение)»; первая строка сохраняет |NUM оригинала, остальные — uuid. */
export async function buildSplitRows(
    selectedRows: TableRow[],
    variants: TableRow[],
    onInitSplitRow?: (row: TableRow, variantIndex: number) => Promise<void>,
): Promise<Array<{ sourceNum: unknown; rows: TableRow[] }>> {
    const result: Array<{ sourceNum: unknown; rows: TableRow[] }> = [];
    for (const selectedRow of selectedRows) {
        const rows: TableRow[] = [];
        for (let index = 0; index < variants.length; index++) {
            const variant = variants[index];
            const row = structuredClone(selectedRow);
            if (index !== 0) row['|NUM'] = uuidv4();
            Object.keys(variant).forEach((propKey) => {
                if (propKey !== '|NUM') {
                    row[propKey] = `${String(row[propKey] ?? '')} (${String(variant[propKey] ?? '')})`;
                }
            });
            await onInitSplitRow?.(row, index);
            rows.push(row);
        }
        result.push({ sourceNum: selectedRow['|NUM'], rows });
    }
    return result;
}
